import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { formatDMY } from "@/lib/dates";
import { toast } from "sonner";
import { TENANTS } from "@/constants/testIds";

const empty = () => ({
  property_id: "", tenant_id: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10),
  monthly_amount: "", currency: "USD",
  deposit: "", adjustment_pct: 0, adjustment_months: 6, payment_day: 5,
  notes: "", status: "active",
});

// Human-readable duration between two YYYY-MM-DD strings.
// Uses inclusive-inclusive semantics (both start_date and end_date are contract days),
// so "01/03/2026 → 28/02/2027" is treated as exactly 12 months (the end date is
// the last day BEFORE the anniversary of the start date).
function durationLabel(startIso, endIso) {
  if (!startIso || !endIso) return "-";
  const a = new Date(startIso);
  const b = new Date(endIso);
  if (isNaN(a.getTime()) || isNaN(b.getTime()) || b < a) return "-";
  // Treat end date as inclusive by adding 1 day, then use standard exclusive math.
  const bPlus1 = new Date(b);
  bPlus1.setDate(bPlus1.getDate() + 1);
  let months = (bPlus1.getFullYear() - a.getFullYear()) * 12 + (bPlus1.getMonth() - a.getMonth());
  if (bPlus1.getDate() < a.getDate()) months -= 1;
  if (months < 0) months = 0;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts = [];
  if (years) parts.push(`${years} año${years > 1 ? "s" : ""}`);
  if (rem) parts.push(`${rem} mes${rem > 1 ? "es" : ""}`);
  return parts.length ? parts.join(" y ") : "menos de 1 mes";
}

export default function ContractDialog({ open, onOpenChange, properties, tenants, defaultTenantId, contract, onSaved }) {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [skipPast, setSkipPast] = useState(true);
  const isEdit = !!contract?.id;

  // Detect if start_date is in the past → offer to skip past payment generation.
  const todayIso = new Date().toISOString().slice(0, 10);
  const startInPast = form.start_date && form.start_date < todayIso;

  useEffect(() => {
    if (!open) return;
    if (contract?.id) {
      setForm({ ...empty(), ...contract, monthly_amount: contract.monthly_amount ?? "" });
      return;
    }
    const f = empty();
    if (defaultTenantId) f.tenant_id = defaultTenantId;
    if (properties.length) {
      f.property_id = properties[0].id;
      f.currency = properties[0].currency;
    }
    setForm(f);
  }, [open, defaultTenantId, properties, contract]);

  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        monthly_amount: parseFloat(form.monthly_amount) || 0,
        deposit: parseFloat(form.deposit) || 0,
        adjustment_pct: parseFloat(form.adjustment_pct) || 0,
        adjustment_months: parseInt(form.adjustment_months) || 6,
        payment_day: parseInt(form.payment_day) || 5,
      };
      if (isEdit) {
        // Only send fields the backend accepts on update (exclude property_id/tenant_id/created_at/id).
        const { id, property_id, tenant_id, created_at, ...updates } = payload;
        await api.put(`/contracts/${contract.id}`, updates);
        toast.success("Contrato actualizado — pagos pendientes regenerados");
      } else {
        await api.post("/contracts", { ...payload, skip_past_payments: startInPast && skipPast });
        toast.success(
          startInPast && skipPast
            ? "Contrato creado — pagos generados solo a partir de hoy"
            : "Contrato creado — pagos generados"
        );
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={TENANTS.contractDialog} className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Editar contrato" : "Nuevo contrato"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Al modificar fechas o renta, se regeneran los pagos pendientes (los ya cobrados se preservan)."
              : "Al guardar se generan automáticamente los pagos mensuales del período."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label>Propiedad</Label>
            <Select value={form.property_id} onValueChange={(v) => {
              set("property_id", v);
              const p = properties.find((x) => x.id === v);
              if (p) set("currency", p.currency);
            }} disabled={isEdit}>
              <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Inquilino</Label>
            <Select value={form.tenant_id} onValueChange={(v) => set("tenant_id", v)} disabled={isEdit}>
              <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
              <SelectContent>
                {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2 rounded-lg border border-border/60 bg-secondary/30 p-3">
            <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Período del contrato</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs text-muted-foreground">Desde</Label>
                <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} required data-testid="contract-form-start-date" />
                <div className="text-[10px] text-muted-foreground mt-0.5 num">{formatDMY(form.start_date)}</div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Hasta</Label>
                <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} required data-testid="contract-form-end-date" />
                <div className="text-[10px] text-muted-foreground mt-0.5 num">{formatDMY(form.end_date)}</div>
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              Duración: <strong className="text-foreground num">{durationLabel(form.start_date, form.end_date)}</strong>
            </div>
            {!isEdit && startInPast && (
              <label className="mt-3 flex items-start gap-2 cursor-pointer rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5">
                <input
                  type="checkbox"
                  checked={skipPast}
                  onChange={(e) => setSkipPast(e.target.checked)}
                  className="mt-0.5 shrink-0"
                  data-testid="contract-form-skip-past"
                />
                <div className="text-xs">
                  <div className="font-semibold text-amber-600">Generar pagos SOLO a partir de hoy</div>
                  <div className="text-muted-foreground mt-0.5">
                    La fecha de inicio es del pasado. Si dejás esto marcado, el sistema NO va a crear pagos retroactivos vencidos — solo los del mes actual en adelante. <strong>Recomendado.</strong>
                  </div>
                </div>
              </label>
            )}
          </div>
          <div>
            <Label>Renta mensual</Label>
            <Input type="number" step="0.01" required value={form.monthly_amount} onChange={(e) => set("monthly_amount", e.target.value)} />
          </div>
          <div>
            <Label>Moneda</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="ARS">ARS</SelectItem>
                <SelectItem value="MXN">MXN</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Depósito</Label>
            <Input type="number" step="0.01" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} />
          </div>
          <div>
            <Label>Día de pago (1–28)</Label>
            <Input type="number" min="1" max="28" value={form.payment_day} onChange={(e) => set("payment_day", e.target.value)} />
          </div>
          <div>
            <Label>Ajuste %</Label>
            <Input type="number" step="0.01" value={form.adjustment_pct} onChange={(e) => set("adjustment_pct", e.target.value)} />
          </div>
          <div>
            <Label>Cada N meses</Label>
            <Input type="number" min="0" value={form.adjustment_months} onChange={(e) => set("adjustment_months", e.target.value)} />
          </div>
          {isEdit && (
            <div className="sm:col-span-2">
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="ended">Finalizado</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>Notas</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving} data-testid="contract-form-submit">
              {saving ? "Guardando…" : (isEdit ? "Guardar cambios" : "Crear contrato y pagos")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
